import { ImpactIndicator } from "@app/projects/enumerators";
import { AggregateDisplayType, SpecialtyGroupEntityType } from "@app/projects/literals";
import {
  AccessSummary,
  MarketSummary,
  ServicingSummary,
  SpecialtyDetailFootnote,
} from "@app/projects/results/adequacy";
import {
  AnalysisSpecialtyGroup,
  ResultRegion,
  SpecialtyGroupException,
} from "@app/projects/results/common";
import {
  CompositeAggregateResultGroup,
  CompositeSummarySpecialtyGroup,
} from "@app/projects/results/composite";
import { RegionGranularity } from "@app/region-sets";

interface CompositeDetail {
  key: string;
  defaultDisplayOrder: number;
  displayText: string;
  linkSuffix: string;

  providerCount: number;
  accuracyIndex: number;
  providerPassPercentage: number;
  providerFailPercentage: number;

  valid?: number;
  deactivated?: number;
  OIGExclusion?: number;
  validated?: number;
  invalidated?: number;
  suspectNumber?: number;
  attestedPercent?: number;
  geocoded?: number;

  distinctNPINumAttested: number;
  distinctNPIPercentAttested: number;
  distinctNPINumUnattested: number;
  numAttested: number;
  numUnattested: number;
  percentAttested: number;
  resultGroups: CompositeAggregateResultGroup[];
  regionCode: string;
  regionSetId: number;
  regionSetDisplayName: string;
  regionGranularity: RegionGranularity;
  subRegionCode: string;

  specialtyGroupCode: string;
  specialtyGroupDisplayName: string;
  specialtyGroupDisplayOrder: number;
  specialtyGroupEntityType?: SpecialtyGroupEntityType;
  specialtyGroupIsInformational: boolean;
  specialtyGroupName?: string;

  dateGapStarted?: Date;
  gapAgeDays?: number;
  impactIndicator: ImpactIndicator;
  metCriteria: boolean;

  //TODO: Flatten these out...yeah there will be a lot of properties in this interface, but it's worth it
  accessSummary: AccessSummary;
  marketSummary: MarketSummary;
  servicingSummary: ServicingSummary;

  recentlyAttestedUniqueNPICount?: number;
  recentlyAttestedUniqueNPIPercent?: number;

  exception?: SpecialtyGroupException | null;

  footnote: SpecialtyDetailFootnote;

  parentAbbreviation?: string;
  parentDisplayName?: string;
}

class CompositeDetailImpl implements CompositeDetail {
  public constructor(
    public key: string,
    private aggregateDisplayType: AggregateDisplayType,

    public providerCount: number,
    public accuracyIndex: number,
    public providerPassPercentage: number,
    public providerFailPercentage: number,

    public valid: number,
    public deactivated: number,
    public OIGExclusion: number,
    public validated: number,
    public invalidated: number,
    public suspectNumber: number,
    public attestedPercent: number,
    public geocoded: number,

    public distinctNPINumAttested: number,
    public distinctNPIPercentAttested: number,
    public distinctNPINumUnattested: number,
    public numAttested: number,
    public numUnattested: number,
    public percentAttested: number,
    public resultGroups: CompositeAggregateResultGroup[],
    public regionCode: string,
    public regionGranularity: RegionGranularity,
    public regionSetDisplayName: string,
    public regionSetId: number,
    public subRegionCode: string,

    public specialtyGroupCode: string,
    public specialtyGroupDisplayName: string,
    public specialtyGroupDisplayOrder: number,
    public specialtyGroupIsInformational: boolean,

    public impactIndicator: ImpactIndicator,
    public metCriteria: boolean,

    public accessSummary: AccessSummary,
    public marketSummary: MarketSummary,
    public servicingSummary: ServicingSummary,

    public footnote: SpecialtyDetailFootnote,

    public recentlyAttestedUniqueNPICount?: number,
    public recentlyAttestedUniqueNPIPercent?: number,

    public dateGapStarted?: Date,
    public exception?: SpecialtyGroupException | null,
    public gapAgeDays?: number,
    public specialtyGroupEntityType?: SpecialtyGroupEntityType,
    public specialtyGroupName?: string,

    public parentAbbreviation?: string,
    public parentDisplayName?: string,
  ) { }

  public get defaultDisplayOrder(): number {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetId;
      case "specialty":
        return this.specialtyGroupDisplayOrder;
      case "unknown":
        return null;
    }
  }

  public get displayText(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetDisplayName;
      case "specialty":
        return this.specialtyGroupDisplayName;
      case "unknown":
        return null;
    }
  }

  public get linkSuffix(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return `/${this.regionGranularity}/${this.regionCode}`;
      case "specialty":
        return `/specialty/${this.specialtyGroupCode}`;
      case "unknown":
        return null;
    }
  }
}

function buildCompositeDetail(
  key: string,
  resultRegion: ResultRegion,
  summarySpecialtyGroup: CompositeSummarySpecialtyGroup,
  specialtyGroup: AnalysisSpecialtyGroup,
  exception: SpecialtyGroupException,
  aggregateDisplayType: AggregateDisplayType,
): CompositeDetail {
  if (!resultRegion || !summarySpecialtyGroup || !specialtyGroup) {
    return {} as CompositeDetail;
  }

  const [valid, deactivated, OIGExclusion, validated, invalidated, suspectNumber, attestedPercent, geocoded]:
  [number, number, number, number, number, number, number, number] = [null, null, null, null, null, null, null, null];

  //if (summarySpecialtyGroup.resultGroups && summarySpecialtyGroup.resultGroups.filter((r) => r.results).length > 0) {
  //  summarySpecialtyGroup.resultGroups.forEach((sg) => {
  //    sg.results.forEach((r) => {
  //      switch (r.name) {
  //        case InspectionResultType.Valid: {
  //          valid = r.value;
  //        }
  //        case InspectionResultType.Deactivated: {
  //          deactivated = r.value;
  //        }
  //        case InspectionResultType.OIGExclusion: {
  //          OIGExclusion = r.value;
  //        }
  //        case InspectionResultType.Validated: {
  //          validated = r.value;
  //        }
  //        case InspectionResultType.Invalidated: {
  //          invalidated = r.value;
  //        }
  //        case InspectionResultType.SuspectNumber: {
  //          suspectNumber = r.value;
  //        }
  //        case InspectionResultType.AttestedPercent: {
  //          attestedPercent = r.value;
  //        }
  //        case InspectionResultType.Geocoded: {
  //          geocoded = r.value;
  //        }
  //      }
  //    });
  //  });
  //}
  return new CompositeDetailImpl(
    key,
    aggregateDisplayType,

    summarySpecialtyGroup.providerCount,
    summarySpecialtyGroup.accuracyIndex,
    summarySpecialtyGroup.providerPassPercentage,
    summarySpecialtyGroup.providerFailPercentage,

    valid,
    deactivated,
    OIGExclusion,
    validated,
    invalidated,
    suspectNumber,
    attestedPercent,
    geocoded,

    summarySpecialtyGroup.distinctNPINumAttested,
    summarySpecialtyGroup.distinctNPIPercentAttested,
    summarySpecialtyGroup.distinctNPINumUnattested,
    summarySpecialtyGroup.numAttested,
    summarySpecialtyGroup.numUnattested,
    summarySpecialtyGroup.percentAttested,
    summarySpecialtyGroup.resultGroups,

    resultRegion.regionCode,
    resultRegion.regionGranularity,
    resultRegion.displayName,
    resultRegion.regionSetId,
    resultRegion.subRegionCode,

    specialtyGroup.code,
    `${specialtyGroup.code} - ${specialtyGroup.name}`,
    specialtyGroup.displayOrder,
    specialtyGroup.isInformational,

    summarySpecialtyGroup.impactIndicator,
    summarySpecialtyGroup.metCriteria,

    summarySpecialtyGroup.accessSummary,
    summarySpecialtyGroup.marketSummary,
    summarySpecialtyGroup.servicingSummary,

    summarySpecialtyGroup.footnote,

    summarySpecialtyGroup.recentlyAttestedUniqueNPICount,
    summarySpecialtyGroup.recentlyAttestedUniqueNPIPercent,

    summarySpecialtyGroup.dateGapStarted,
    exception,
    summarySpecialtyGroup.gapAgeDays,

    specialtyGroup.entityType,
    specialtyGroup.name,
  );
}

/**
 * Clones an `CompositeDetail` while setting a new displayText property based on the `AdequacyAggregateDisplayType`
 * It DOES actually create a new object
 * @param compositeDetail
 * @param aggregateDisplayType
 */
function pseudoClone(
  compositeDetail: CompositeDetail,
  aggregateDisplayType: AggregateDisplayType,
): CompositeDetail {

  return new CompositeDetailImpl(
    compositeDetail.key,
    aggregateDisplayType,

    compositeDetail.providerCount,
    compositeDetail.accuracyIndex,
    compositeDetail.providerPassPercentage,
    compositeDetail.providerFailPercentage,

    compositeDetail.valid,
    compositeDetail.deactivated,
    compositeDetail.OIGExclusion,
    compositeDetail.validated,
    compositeDetail.invalidated,
    compositeDetail.suspectNumber,
    compositeDetail.attestedPercent,
    compositeDetail.geocoded,

    compositeDetail.distinctNPINumAttested,
    compositeDetail.distinctNPIPercentAttested,
    compositeDetail.distinctNPINumUnattested,
    compositeDetail.numAttested,
    compositeDetail.numUnattested,
    compositeDetail.percentAttested,
    compositeDetail.resultGroups,

    compositeDetail.regionCode,
    compositeDetail.regionGranularity,
    compositeDetail.regionSetDisplayName,
    compositeDetail.regionSetId,
    compositeDetail.subRegionCode,

    compositeDetail.specialtyGroupCode,
    compositeDetail.specialtyGroupDisplayName,
    compositeDetail.specialtyGroupDisplayOrder,
    compositeDetail.specialtyGroupIsInformational,

    compositeDetail.impactIndicator,
    compositeDetail.metCriteria,

    compositeDetail.accessSummary,
    compositeDetail.marketSummary,
    compositeDetail.servicingSummary,

    compositeDetail.footnote,

    compositeDetail.recentlyAttestedUniqueNPICount,
    compositeDetail.recentlyAttestedUniqueNPIPercent,

    compositeDetail.dateGapStarted,
    compositeDetail.exception,
    compositeDetail.gapAgeDays,

    compositeDetail.specialtyGroupEntityType,
    compositeDetail.specialtyGroupName,
  );

}

export { CompositeDetail, buildCompositeDetail, pseudoClone };
